import type { Facet } from '@/state/experiments/library-learnings';
import { useState } from 'react';
import { Check, SlidersHorizontal } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useExperimentsList } from '@/state/experiments/useExperimentsList';
import { cn } from 'helpers/cn';
import { FOCUS_RING } from '../../ConsolePanel';

type LearningsFilterProps = { manager: ReturnType<typeof useExperimentsList> };

/**
 * Outcome, expected mechanism, change type, theme and element, behind one button beside the search.
 *
 * They were two always-visible chip rows under the views, which crowded the page for a
 * filter used now and then. Folded into a popover, they cost one button until opened;
 * the button carries the count of active filters, so a filtered table is never
 * unexplained.
 *
 * Disabled rather than hidden when nothing on the view can be filtered, so the control
 * does not appear and disappear as the views change.
 */
export const LearningsFilter = ({ manager }: LearningsFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { outcomes, mechanisms, changeTypes, outcome, setOutcome, mechanism, setMechanism } = manager;
  const { changeType, setChangeType, elements, element, setElement, themes, theme, setTheme } = manager;
  const active = [outcome, mechanism, changeType, element, theme].filter(Boolean).length;
  const nothingToFilter =
    outcomes.length === 0 &&
    mechanisms.length === 0 &&
    changeTypes.length === 0 &&
    elements.length === 0 &&
    themes.length === 0 &&
    active === 0;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        disabled={nothingToFilter}
        data-tour="experiments-filter"
        className={cn(
          'flex h-9 shrink-0 items-center justify-center gap-2 rounded-lg border px-3 text-meta',
          'transition-colors duration-150 disabled:opacity-50',
          FOCUS_RING,
          active > 0
            ? 'bg-sel-bg border-sel-line text-foreground'
            : 'border-input text-dim hover:bg-hover hover:text-foreground',
        )}
      >
        <SlidersHorizontal className="size-3.5" strokeWidth={1.5} />
        Filter
        {active > 0 && (
          <>
            <span aria-hidden="true" className="tabular-nums">
              · {active}
            </span>
            <span className="sr-only">, {active} active</span>
          </>
        )}
      </PopoverTrigger>

      {/* Scrolls: a website with many tests has many element names. */}
      <PopoverContent align="end" aria-label="Filter experiments" className="max-h-[28rem] w-72 overflow-y-auto p-0">
        <FacetSection label="Outcome" facets={outcomes} selected={outcome} onSelect={setOutcome} />
        <FacetSection label="Change type" facets={changeTypes} selected={changeType} onSelect={setChangeType} />
        <FacetSection label="Theme" facets={themes} selected={theme} onSelect={setTheme} />
        <FacetSection label="Element" facets={elements} selected={element} onSelect={setElement} />
        <FacetSection label="Expected mechanism" facets={mechanisms} selected={mechanism} onSelect={setMechanism} />
        <div className="border-line flex justify-end border-t px-2 py-1.5">
          {/* Never disabled: disabling the focused button on click dropped focus to the
              page body, outside the popover, and a keyboard user lost their place. */}
          <button
            type="button"
            aria-disabled={active === 0}
            onClick={manager.clearLearningFilters}
            className={cn(
              'text-dim hover:text-foreground rounded-md px-2 py-1 text-meta aria-disabled:opacity-40',
              FOCUS_RING,
            )}
          >
            Clear
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

type FacetSectionProps<K extends string> = {
  label: string;
  facets: Facet<K>[];
  selected: string;
  /** null clears the filter, which removes it from the URL rather than leaving it empty. */
  onSelect: (value: string | null) => void;
};

/** Single choice per section; choosing the selected option again clears it. */
const FacetSection = <K extends string>({ label, facets, selected, onSelect }: FacetSectionProps<K>) => {
  const labelId = `facet-${label.toLowerCase().replace(/\s+/g, '-')}`;
  if (facets.length === 0) return null;

  return (
    <div role="group" aria-labelledby={labelId} className="border-line border-b py-1.5 last:border-b-0">
      <p id={labelId} className="micro-label px-3 pb-1 pt-1">
        {label}
      </p>
      {facets.map((facet) => {
        const isSelected = facet.value === selected;
        return (
          <button
            key={facet.value}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onSelect(isSelected ? null : facet.value)}
            className={cn(
              'flex w-full items-center gap-2 px-3 py-1.5 text-left text-meta transition-colors duration-150',
              FOCUS_RING,
              isSelected ? 'bg-sel-bg text-foreground' : 'text-foreground hover:bg-hover',
            )}
          >
            <Check className={cn('size-3.5 shrink-0', isSelected ? 'text-accent' : 'invisible')} strokeWidth={2} />
            <span className="min-w-0 flex-1 truncate">{facet.label}</span>
            <span className="text-dim tabular-nums">{facet.count}</span>
          </button>
        );
      })}
    </div>
  );
};
